import { ModalNew as Modal } from "components/Modal/ModalNew";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { DocumentForm } from "components/DocumentForm/DocumentForm";
import { addDocument } from "actions/companyActions";
import { showToast } from "utils/helpers";
import { useCurrentCompany } from "hooks/auth";

interface DocumentCategory {
	label: string;
	value: number;
}

interface AllowedCategory {
	category_id: string;
	subcategory_id?: string;
}

interface AddNewDocumentModalProps {
	allowedCategories: AllowedCategory[];
	allowedCategoryIds: number[];
	categories: DocumentCategory[];
	isOpen: boolean;
	onClose: () => void;
	onCreated: (id: number) => void;
}

export const AddNewDocumentModal: React.FC<AddNewDocumentModalProps> = ({
	isOpen,
	onClose,
	onCreated,
	categories,
	allowedCategoryIds,
	allowedCategories,
}) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const currentCompany = useCurrentCompany();
	const companyId = (currentCompany as { id?: number })?.id;
	const profileTypeId = (currentCompany as { profile_type?: { id?: number } })
		?.profile_type?.id;

	const lockCategory = allowedCategoryIds.length === 1;
	const lockedSubcategoryId = lockCategory && allowedCategories.length === 1
		? allowedCategories[0].subcategory_id
			? Number(allowedCategories[0].subcategory_id)
			: null
		: null;
	const lockSubcategory = lockedSubcategoryId !== null;

	const initialValues = {
		name: "",
		category_id:
			allowedCategoryIds.length === 1 ? allowedCategoryIds[0] : "",
		sub_category_id: "",
		documentable_type: "App\\Models\\Company",
		file: "",
		extra_info: {
			expiry_date: "",
			accreditation: "",
			relevant_year: "",
			value: "",
			audited: "",
		},
	};

	const handleSubmit = async (
		values: Record<string, unknown>,
		{ setErrors }: { setErrors: (errors: Record<string, string>) => void }
	) => {
		try {
			const result = await (dispatch as { (dispatch): { id; data, } })(
				addDocument(companyId, values)
			);
			const newId = result?.data?.id ?? result?.id;
			queryClient.invalidateQueries({ queryKey: ["corporateDocument"] });
			if (newId) {
				onCreated(newId);
			}
			onClose();
			showToast("Document added successfully.", "Success", true);
		} catch (e: unknown) {
			if (e && typeof e === "object" && "response" in e) {
				const error = await (
					e as { response: { json: () => Promise<{ errors: Record<string, string>; message: string }> } }
				).response.json();
				setErrors(error.errors);
				showToast(error.message, "Error");
			} else {
				showToast("An unexpected error occurred.", "Error");
			}
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			headerContent="Add New Document"
		>
			{/* @ts-ignore */}
			<DocumentForm
				initialValues={initialValues}
				profileTypeId={profileTypeId}
				handleSubmit={handleSubmit}
				title=""
				lockCategory={lockCategory}
				lockSubcategory={lockSubcategory}
				lockedSubcategoryId={lockedSubcategoryId}
			/>
		</Modal>
	);
};
